<script lang="ts">
    import type {ReadOnlyReactivePageableList} from '../../shared/utils/reactive-pageable-list';
    import type {
        RecordCollectionItem,
        UUID,
        ViewableRecordCollection
    } from '../../typings/portaro.be.types';
    import type {LocalizeFunction} from '../../svelte/types';
    import {createEventDispatcher, getContext, onDestroy, onMount} from 'svelte';
    import {fly} from 'svelte/transition';
    import {flip} from 'svelte/animate';
    import {CONTEXT_PATH_KEY, LOCALIZE_KEY} from '../../svelte/utils';
    import {pipe} from '../../svelte/utils';
    import {loc} from '../../svelte/pipes';
    import KpDocumentMini from '../../record/kp-document-mini/KpDocumentMini.svelte';
    import KpLoadingInline from '../../commons/components/kp-loading/KpLoadingInline.svelte';
    import KpLoadingBlock from '../../commons/components/kp-loading/KpLoadingBlock.svelte';
    import {disabledWhen} from '../../svelte/svelte-actions/disabled-when';
    import type {RecordCollectionItemEventMap} from './events';
    import {MOVE_DOWN_EVENT_TYPE, MOVE_UP_EVENT_TYPE, REMOVE_EVENT_TYPE} from './events';
    import KpGenericTable from '../../commons/ui-components/table/KpGenericTable.svelte';
    import KpDropdownButton from '../../commons/ui-components/dropdown/KpDropdownButton.svelte';
    import KpDropdownMenuItem from '../../commons/ui-components/dropdown/KpDropdownMenuItem.svelte';
    import type {Pageable} from '../../typings/portaro.fe.types';
    import KpDropdownMenuLink from '../../commons/ui-components/dropdown/KpDropdownMenuLink.svelte';
    import {skipWhile} from 'rxjs/operators';

    export let pageableList: ReadOnlyReactivePageableList<RecordCollectionItem, UUID>;
    export let recordCollection: ViewableRecordCollection;

    const dispatch = createEventDispatcher<RecordCollectionItemEventMap>();
    const localize: LocalizeFunction = getContext(LOCALIZE_KEY);
    const CONTEXT_PATH: string = getContext(CONTEXT_PATH_KEY);

    let items: RecordCollectionItem[] = [];
    let isLoadedAll: boolean;
    let isLoadingNextPage = false;
    let isLoadingFirstPage = true;
    let isInitialized = false;

    let accentedPositions;
    $: accentedPositions = calculateAccentedPositions(items);

    // subscribe for updates after initial load in case already loaded items are not valid (e.g. incorrect sorting order)
    const listItemsSubscription = pageableList.getLoadedItem$().pipe(skipWhile(() => !isInitialized)).subscribe((updatedItems) => updateListItems(updatedItems));
    const listPagesSubscription = pageableList.getCurrentPage$().subscribe((state) => updateListLoadingState(state));

    onMount(async () => {
        items = await pageableList.loadItems();
        isLoadingFirstPage = false;
        isInitialized = true;
    });

    onDestroy(() => {
        listItemsSubscription.unsubscribe();
        listPagesSubscription.unsubscribe();
    });

    function remove(item: RecordCollectionItem) {
        dispatch(REMOVE_EVENT_TYPE, {item});
    }

    function moveUp(item: RecordCollectionItem) {
        dispatch(MOVE_UP_EVENT_TYPE, {item});
    }

    function moveDown(item: RecordCollectionItem) {
        dispatch(MOVE_DOWN_EVENT_TYPE, {item});
    }

    function updateListItems(updatedItems: RecordCollectionItem[]) {
        items = updatedItems;
        isLoadedAll = pageableList.isLoadedAll();
        isLoadingFirstPage = false;
    }

    function updateListLoadingState(page: Pageable) {
        // in case of list reload, show loading animation
        if (page.pageNumber === 1) {
            isLoadingFirstPage = true;
        }
    }

    async function loadNextPage() {
        isLoadingNextPage = true;
        await pageableList.loadNext();
        isLoadingNextPage = false;
    }

    function calculateAccentedPositions(newItems: RecordCollectionItem[]): boolean[] {
        const result: boolean[] = [];

        for (let i = 0; i < newItems.length; i++) {
            if (i === 0) {
                result[i] = false;
                continue;
            }
            const prev =  newItems[i - 1].recordCollectionId;
            const curr = newItems[i].recordCollectionId;

            if (curr !== prev) {
                result[i] = !result[i - 1];
            } else {
                result[i] = result[i - 1];
            }
        }
        return result;
    }

    function isAccented(index: number): boolean {
        return accentedPositions[index];
    }

    function canMoveUp(item: RecordCollectionItem, index: number): boolean {
        return index > 0 && items[index - 1].recordCollectionId === item.recordCollectionId && recordCollection.editable; // if top level collection is editable so are subcollections
    }

    function canMoveDown(item: RecordCollectionItem, index: number): boolean {
        return index < items.length - 1 && items[index + 1].recordCollectionId === item.recordCollectionId && recordCollection.editable; // if top level collection is editable so are subcollections
    }

    function canRemove(): boolean {
        return recordCollection.editable;
    }

    function showSubcollectionLink(item: RecordCollectionItem): boolean {
        return recordCollection.id !== item.recordCollectionId;
    }

    function isContextMenuDisabled(item: RecordCollectionItem, index: number): boolean {
        return !(canMoveUp(item, index) || canMoveDown(item, index) || canRemove() || showSubcollectionLink(item));
    }

</script>

<template>
    {#if isLoadingFirstPage}
        <KpLoadingBlock/>
    {:else }
        <KpGenericTable condensed={true} hoverRows={true}>
            <tr slot="header">
                <th>{localize(/* @kp-localization commons.nazevDokumentu */ 'commons.nazevDokumentu')}</th>
                <th>{localize(/* @kp-localization commons.fond */ 'commons.fond')}</th>
                <th>{localize(/* @kp-localization commons.Options */ 'commons.Options')}</th>
            </tr>
            <svelte:fragment slot="body">
                {#each items as item, index (item.id)}
                    <tr class:accented-item={isAccented(index)} in:fly|local={{duration: 200, y: 50, delay: 200}} animate:flip={{duration: 200}}> <!-- TODO: find better workaround for svelte transition+animation bug: https://github.com/sveltejs/svelte/issues/4910 -->
                        <td>
                            <KpDocumentMini document={item.record}/>
                        </td>
                        <td>
                            {pipe(item.record.fond, loc())}
                        </td>
                        <td>
                            <KpDropdownButton buttonSize="sm" menuAlignment="right" isDisabled={isContextMenuDisabled(item, index)}>
                                <svelte:fragment slot="button">
                                    <span class="glyphicon glyphicon-menu-hamburger"></span>
                                </svelte:fragment>
                                <svelte:fragment slot="menu">
                                    <KpDropdownMenuItem on:click={() => moveUp(item)} showWhen={canMoveUp(item, index)}>
                                        <span class="glyphicon glyphicon-arrow-up"></span>
                                        {localize(/* @kp-localization commons.MoveUp */ 'commons.MoveUp')}
                                    </KpDropdownMenuItem>
                                    <KpDropdownMenuItem on:click={() => moveDown(item)} showWhen={canMoveDown(item, index)}>
                                        <span class="glyphicon glyphicon-arrow-down"></span>
                                        {localize(/* @kp-localization commons.MoveDown */ 'commons.MoveDown')}
                                    </KpDropdownMenuItem>
                                    <KpDropdownMenuItem on:click|once={() => remove(item)} showWhen={canRemove()}>
                                        <span class="glyphicon glyphicon-trash"></span>
                                        {localize(/* @kp-localization commons.odebrat */ 'commons.odebrat')}
                                    </KpDropdownMenuItem>
                                    <KpDropdownMenuLink href="{CONTEXT_PATH}/#!/record-collections/{item.recordCollectionId}" showWhen={showSubcollectionLink(item)}>
                                        <span class="glyphicon glyphicon-new-window"></span>
                                        <span title={item.recordCollectionId}>
                                            {localize(/* @kp-localization recordCollections.ShowSubcollection */ 'recordCollections.ShowSubcollection')}
                                        </span>
                                    </KpDropdownMenuLink>
                                </svelte:fragment>
                            </KpDropdownButton>
                        </td>
                    </tr>
                {/each}
            </svelte:fragment>
        </KpGenericTable>
        {#if !isLoadedAll}
            <button type="button"
                    class="btn btn-default btn-block btn-sm"
                    use:disabledWhen={isLoadingNextPage}
                    on:click={loadNextPage}>
                <span class:hidden={isLoadingNextPage}>
                    {localize(/* @kp-localization vysledky.NacistDalsi */ 'vysledky.NacistDalsi')}
                </span>
                <KpLoadingInline size="xs" showWhen={isLoadingNextPage}/>
            </button>
        {/if}
    {/if}
</template>

<style lang="less">
  @import "bootstrap-less/bootstrap/variables";

  .hidden {
      display: none;
    }

    .accented-item {
      background-color: @table-bg-accent;
    }
</style>